import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { JwtPayload } from 'jwt-decode';
import { IndicatorState } from 'src/app/shared/components/sb-pending-button-indicator/sb-pending-button-indicator.component';

// TODO: clean up store and decide on structure for session info, eg. should scope be part of JWT or me request? Having both is really confusing...
export type SessionMessage = {
    payload: {
        type: 'LOGOUT' | 'TOKEN_EXCHANGE';
        userId: number;
        trigger?: logoutReason;
    };
};

interface TypedBroadcastChannelEventMap<T> {
    message: MessageEvent<T>;
    messageerror: MessageEvent<T>;
}

export interface TypedBroadcastChannel<T> extends EventTarget {
    readonly name: string;
    onmessage: ((this: BroadcastChannel, ev: MessageEvent<T>) => any) | null;
    onmessageerror: ((this: BroadcastChannel, ev: MessageEvent<T>) => any) | null;
    close(): void;
    postMessage(message: T): void;
    addEventListener<K extends keyof TypedBroadcastChannelEventMap<T>>(
        type: K,
        listener: (
            this: BroadcastChannel,
            ev: TypedBroadcastChannelEventMap<T>[K]
        ) => any,
        options?: boolean | AddEventListenerOptions
    ): void;
    addEventListener(
        type: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions
    ): void;
    removeEventListener<K extends keyof TypedBroadcastChannelEventMap<T>>(
        type: K,
        listener: (
            this: BroadcastChannel,
            ev: TypedBroadcastChannelEventMap<T>[K]
        ) => any,
        options?: boolean | EventListenerOptions
    ): void;
    removeEventListener(
        type: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | EventListenerOptions
    ): void;
}

export type logoutReason =
    | 'userAction' // when user actively logs out
    | 'sessionTimeOut' // when session times out
    | 'invalidAccessToken' // when access token is invalid
    | 'invalidRefreshToken' // when refresh token is invalid
    | 'newVersion'; // when new version is available (chunk load error)

export interface DecodedToken extends JwtPayload {
    typ?: 'Refresh' | string;
    azp?: 'swip';
    scope?: string;
    sid: string;
    session_state?: string;
    exp: number;
    iat: number;
    jti: string;
    iss: string;
    aud: string;
    sub: string;
    acr: string;
    'allowed-origins': Array<string>;
    realm_access: {
        // TODO: add strict role types
        roles: Array<string>;
    };
    resource_access: {
        account: {
            // TODO: add strict role types
            roles: Array<string>;
        };
    };
    email_verified: boolean;
    ownerIds: string; // stringified array
    accountIds: string; // stringified array
    user_name: string;
    primaryOwnerId: string;
    name: string;
    preferred_username: string;
    given_name: string;
    family_name: string;
    userId: number;
    email: string;
}

export function isTokenResponse(response: any): response is TokenResponse {
    return response.access_token !== undefined;
}

export interface TokenResponse {
    access_token: string;
    expires_in: number;
    refresh_expires_in: number;
    refresh_token: string;
    token_type: 'Bearer' | string;
    id_token: string;
    'not-before-policy': number;
    session_state: string;
    scope: string;
    error: string;
    error_description: string;
    error_uri: string;
}

export interface SessionState {
    /** Set from token request */
    access_token: string | null;
    error?: any;
    error_description?: any;
    error_uri?: any;
    expires_in: number | null;
    id_token?: any;
    'not-before-policy'?: number;
    token_type: string | null;
    refresh_token: string | null;
    refresh_expires_in?: number | null;
    scope: string;
    session_state?: string;
    /*twoFactorStatus: {
        primary?: string;
        secondary?: string;
        recoveryCodes?: any;
    };*/
    user?: MeInfo; // TODO: should be refactored
    /** Set on user interaction TODO: move to UI */
    interaction?: boolean;
    loadingState?: IndicatorState;
    decodedToken: DecodedToken;
}

export interface MeInfo {
    details?: {
        decodedDetails?: {
            authorities?: string[] | null;
            user_name?: string;
        };
    };
    oauth2Request?: {
        scope?: string[] | null;
    };
    totpStatus: {
        twoFactorRequired: {
            primary?: string;
            secondary?: string;
            recoveryCodes?: any;
        };
    };
}

export interface ImpersonationToken {
    access_token: string;
    error: null;
    error_description: null;
    error_uri: null;
    expires_in: number;
    id_token: null;
    'not-before-policy': number;
    refresh_expires_in: number;
    refresh_token: string;
    scope: string;
    session_state: string;
    token_type: 'Bearer';
}

export function createInitialState(): SessionState {
    return {
        access_token: null,
        expires_in: null,
        refresh_token: null,
        token_type: null,
        interaction: false,
        loadingState: null,
        scope: null,
        decodedToken: null
        //twoFactorStatus: null
    };
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'session', resettable: true })
export class SessionStore extends Store<SessionState> {
    public readonly sessionChannel: TypedBroadcastChannel<SessionMessage> =
        new BroadcastChannel('swip-session');
    constructor() {
        super(createInitialState());
    }

    setLoadingState(loadingState: IndicatorState) {
        this.update((state) => ({ ...state, loadingState }));
    }
}
